'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, XAxis, YAxis } from 'recharts';

import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { formatMoney } from "@/lib/money";

import type { AnalyticsDataPoint } from './ChartContainer';
import { EmptyState } from './EmptyState';
import { formatBucketDate } from '../utils/formatBucketDate';

export type RevenueCompositionViewMode = 'timeSeries' | 'aggregated';

interface RevenueCompositionChartProps {
    straightSaleData: AnalyticsDataPoint[];
    rebillData: AnalyticsDataPoint[];
    upsellData: AnalyticsDataPoint[];
    currency: string;
    className?: string;
    viewMode?: RevenueCompositionViewMode;
}

const REVENUE_TYPE_COLOR_KEYS: Record<string, string> = {
    'straightSale': 'var(--chart-1)',
    'rebill': 'var(--chart-2)',
    'upsell': 'var(--chart-3)',
};

export function RevenueCompositionChart({ straightSaleData, rebillData, upsellData, currency, className = '', viewMode = 'timeSeries' }: RevenueCompositionChartProps) {
    const { t } = useTranslation('analytics');
    const timeSeriesData = React.useMemo(() => {
        const allDates = new Set<string>();
        [straightSaleData, rebillData, upsellData].forEach((data) => { data.forEach((item) => allDates.add(item.code)); });
        return Array.from(allDates).sort().map((date) => {
            const straightSale = straightSaleData.find((i) => i.code === date);
            const rebill = rebillData.find((i) => i.code === date);
            const upsell = upsellData.find((i) => i.code === date);
            return { date, straightSale: straightSale?.value || 0, rebill: rebill?.value || 0, upsell: upsell?.value || 0 };
        });
    }, [straightSaleData, rebillData, upsellData]);

    const aggregatedData = React.useMemo(() => {
        const straightSaleTotal = straightSaleData.reduce((sum, item) => sum + (item.value || 0), 0);
        const rebillTotal = rebillData.reduce((sum, item) => sum + (item.value || 0), 0);
        const upsellTotal = upsellData.reduce((sum, item) => sum + (item.value || 0), 0);
        const straightSaleVolume = straightSaleData.reduce((sum, item) => sum + (item.volume || 0), 0);
        const rebillVolume = rebillData.reduce((sum, item) => sum + (item.volume || 0), 0);
        const upsellVolume = upsellData.reduce((sum, item) => sum + (item.volume || 0), 0);
        return [
            { name: t('charts.straightSale'), key: 'straightSale', value: straightSaleTotal, volume: straightSaleVolume },
            { name: t('charts.rebill'), key: 'rebill', value: rebillTotal, volume: rebillVolume },
            { name: t('charts.upsell'), key: 'upsell', value: upsellTotal, volume: upsellVolume },
        ].filter(item => item.value > 0).sort((a, b) => b.value - a.value);
    }, [straightSaleData, rebillData, upsellData]);

    const chartData = viewMode === 'aggregated' ? aggregatedData : timeSeriesData;

    const formatYAxisValue = (value: number) => {
        const majorUnits = value / 100;
        if (majorUnits >= 1000000) return `${(majorUnits / 1000000).toFixed(1)}M`;
        if (majorUnits >= 1000) return `${(majorUnits / 1000).toFixed(1)}K`;
        return `${majorUnits.toFixed(0)}`;
    };

    const labelFormatter = (label: string) => {
        return formatBucketDate(label, { month: 'short', day: 'numeric' });
    };

    const TimeSeriesCustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || payload.length === 0) return null;
        const total = payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0);
        const reversed = [...payload].reverse();
        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{labelFormatter(String(label))}</p>
                <div className="space-y-1">
                    {reversed.map((entry, idx) => {
                        const value = Number(entry.value) || 0;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                        return (
                            <div key={idx} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
                                    <span className="text-sm text-muted-foreground">{({ straightSale: t('charts.straightSale'), rebill: t('charts.rebill'), upsell: t('charts.upsell') } as Record<string, string>)[entry.name] || entry.name}:</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-medium text-card-foreground">{formatMoney(value, currency)}</span>
                                    <span className="text-xs text-muted-foreground ml-2">({percentage}%)</span>
                                </div>
                            </div>
                        );
                    })}
                    <div className="pt-2 mt-2 border-t border-border">
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-sm font-medium text-muted-foreground">{t('charts.total')}:</span>
                            <span className="text-sm font-semibold text-card-foreground">{formatMoney(total, currency)}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const AggregatedCustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || payload.length === 0) return null;
        const item = payload[0].payload;
        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{item.name}</p>
                <div className="space-y-1">
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{t('charts.revenue')}:</span>
                        <span className="text-sm font-medium text-card-foreground">{formatMoney(item.value, currency)}</span>
                    </div>
                    {item.volume > 0 && (
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-sm text-muted-foreground">{t('charts.transactions')}:</span>
                            <span className="text-sm font-medium text-card-foreground">{item.volume.toLocaleString()}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (!chartData.length) {
        return <EmptyState message={t('charts.noRevenueData')} className={className} />;
    }

    if (viewMode === 'aggregated') {
        return (
            <div className={`h-full ${className}`}>
                <ChartContainer
                    config={{
                        straightSale: { label: t('charts.straightSale'), color: 'var(--chart-1)' },
                        rebill: { label: t('charts.rebill'), color: 'var(--chart-2)' },
                        upsell: { label: t('charts.upsell'), color: 'var(--chart-3)' },
                    }}
                    className="w-full h-full"
                >
                    <BarChart
                        data={aggregatedData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    >
                        <XAxis type="number" tickFormatter={formatYAxisValue} />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={90}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <ChartTooltip cursor={false} content={<AggregatedCustomTooltip />} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {aggregatedData.map((entry) => (
                                <Cell key={entry.name} fill={REVENUE_TYPE_COLOR_KEYS[entry.key] || 'var(--chart-1)'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ChartContainer>
            </div>
        );
    }

    return (
        <div className={`h-full ${className}`}>
            <ChartContainer config={{ straightSale: { label: t('charts.straightSale'), color: 'var(--chart-1)' }, rebill: { label: t('charts.rebill'), color: 'var(--chart-2)' }, upsell: { label: t('charts.upsell'), color: 'var(--chart-3)' } }} className="w-full h-full">
                <BarChart accessibilityLayer data={timeSeriesData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} />
                    <YAxis tickFormatter={formatYAxisValue} />
                    <ChartTooltip cursor={false} content={<TimeSeriesCustomTooltip />} />
                    <Bar dataKey="upsell" stackId="revenue" fill="var(--color-upsell)" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="rebill" stackId="revenue" fill="var(--color-rebill)" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="straightSale" stackId="revenue" fill="var(--color-straightSale)" radius={[4, 4, 0, 0]} />
                </BarChart>
            </ChartContainer>
        </div>
    );
}


